import React, { useState } from 'react';

const SyntheticAssetCreator = ({ selectedAccount, onAssetCreated, onClose }) => {
  const [formData, setFormData] = useState({
    symbol: '',
    side: '',
    currency: '',
    exchange: '',
    sectype: '',
    leverage: 1.0,
    isAssetActive: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'currency':
        if (value && value.length > 3) {
          newErrors.currency = 'Currency code must be 3 characters max (e.g., USD, EUR)';
        } else {
          delete newErrors.currency;
        }
        break;
      case 'sectype':
        if (value && value.length > 7) {
          newErrors.sectype = 'Security type must be 7 characters max (e.g., STK, FUT)';
        } else {
          delete newErrors.sectype;
        }
        break;
      case 'symbol':
        if (value && value.length > 50) {
          newErrors.symbol = 'Symbol must be 50 characters max';
        } else {
          delete newErrors.symbol;
        }
        break;
      case 'exchange':
        if (value && value.length > 50) {
          newErrors.exchange = 'Exchange must be 50 characters max';
        } else {
          delete newErrors.exchange;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const allValid = Object.keys(formData).every(field => validateField(field, formData[field]));
    
    if (!allValid || Object.keys(errors).length > 0) {
      alert('Please fix validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        symbol: formData.symbol.trim(),
        broker: selectedAccount.broker,
        account: selectedAccount.broker === 'ib' ? selectedAccount.account : null,
        isAssetActive: formData.isAssetActive
      };

      if (formData.side) dataToSend.side = formData.side;
      if (formData.currency) dataToSend.currency = formData.currency.toUpperCase();
      if (formData.exchange) dataToSend.exchange = formData.exchange;
      if (formData.sectype) dataToSend.sectype = formData.sectype.toUpperCase();
      if (selectedAccount.broker === 'okx' && formData.leverage) {
        dataToSend.leverage = formData.leverage;
      }

      const { createSyntheticAsset } = await import('../../services/api');
      const result = await createSyntheticAsset(dataToSend);

      if (result) {
        if (result.transferred) {
          alert('Asset created and automatically transferred to main instruments!');
        } else {
          alert('Synthetic asset created successfully! Complete the missing fields to transfer it to main instruments.');
        }
        onAssetCreated && onAssetCreated(dataToSend);
        setFormData({
          symbol: '',
          side: '',
          currency: '',
          exchange: '',
          sectype: '',
          leverage: 1.0,
          isAssetActive: true
        });
        setErrors({});
        onClose && onClose();
      } else {
        alert('Failed to create synthetic asset');
      }
    } catch (error) {
      console.error('Error creating synthetic asset:', error);
      alert('Failed to create synthetic asset: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          Create Synthetic Asset
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Symbol */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Symbol *</label>
            <input
              type="text"
              value={formData.symbol}
              onChange={(e) => handleInputChange('symbol', e.target.value)}
              className={`w-full p-2 border rounded-md ${errors.symbol ? 'border-red-500' : 'border-gray-300'}`}
              required
              placeholder="e.g., AAPL, BTC/USDT"
            />
            {errors.symbol && <p className="text-red-500 text-xs mt-1">{errors.symbol}</p>}
          </div>

          {/* Side */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Side *</label>
            <select 
              value={formData.side || ''}
              onChange={(e) => handleInputChange('side', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Select side</option>
              <option value="BUY">Buy</option>
              <option value="SELL">Sell</option>
            </select>
          </div>

          {/* Currency (for IB) - Optional */}
          {selectedAccount.broker === 'ib' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency (3 chars max)</label>
              <input
                type="text"
                value={formData.currency || ''}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className={`w-full p-2 border rounded-md ${errors.currency ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="USD, EUR, GBP"
                maxLength={3}
              />
              {errors.currency && <p className="text-red-500 text-xs mt-1">{errors.currency}</p>}
            </div>
          )}

          {/* Exchange (for IB) - Optional */}
          {selectedAccount.broker === 'ib' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exchange</label>
              <input
                type="text"
                value={formData.exchange || ''}
                onChange={(e) => handleInputChange('exchange', e.target.value)}
                className={`w-full p-2 border rounded-md ${errors.exchange ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="NASDAQ, NYSE, CME"
              />
              {errors.exchange && <p className="text-red-500 text-xs mt-1">{errors.exchange}</p>}
            </div>
          )}

          {/* Sectype (for IB) - Optional */}
          {selectedAccount.broker === 'ib' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sectype (7 chars max)</label>
              <input
                type="text"
                value={formData.sectype || ''}
                onChange={(e) => handleInputChange('sectype', e.target.value)}
                className={`w-full p-2 border rounded-md ${errors.sectype ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="e.g., STK, FUT, OPT, CFD"
                maxLength={7}
              />
              {errors.sectype && <p className="text-red-500 text-xs mt-1">{errors.sectype}</p>}
            </div>
          )}

          {/* Leverage (for OKX) - Optional */}
          {selectedAccount.broker === 'okx' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Leverage</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                max="100"
                value={formData.leverage || 1.0}
                onChange={(e) => handleInputChange('leverage', parseFloat(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          )}

          {/* Asset Active */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isAssetActive || false}
                onChange={(e) => handleInputChange('isAssetActive', e.target.checked)}
                className="mr-2"
              />
              Asset Active
            </label>
          </div>

          {/* Progress indicator */}
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <h4 className="font-medium mb-2">Field Status:</h4>
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span>Symbol (required):</span>
                <span className={formData.symbol ? 'text-green-600' : 'text-red-600'}>
                  {formData.symbol ? '✓' : '✗'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Side (required):</span>
                <span className={formData.side ? 'text-green-600' : 'text-red-600'}>
                  {formData.side ? '✓' : '✗'}
                </span>
              </div>
              {selectedAccount.broker === 'ib' && (
                <>
                  <div className="flex justify-between">
                    <span>Currency (optional):</span>
                    <span className={formData.currency && !errors.currency ? 'text-green-600' : 'text-gray-500'}>
                      {formData.currency && !errors.currency ? '✓' : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Exchange (optional):</span>
                    <span className={formData.exchange && !errors.exchange ? 'text-green-600' : 'text-gray-500'}>
                      {formData.exchange && !errors.exchange ? '✓' : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sectype (optional):</span>
                    <span className={formData.sectype && !errors.sectype ? 'text-green-600' : 'text-gray-500'}>
                      {formData.sectype && !errors.sectype ? '✓' : '-'}
                    </span>
                  </div>
                </>
              )}
              {selectedAccount.broker === 'okx' && (
                <div className="flex justify-between">
                  <span>Leverage (optional):</span>
                  <span className={formData.leverage ? 'text-green-600' : 'text-gray-500'}>
                    {formData.leverage ? '✓' : '-'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
              disabled={loading || !formData.symbol.trim() || !formData.side || Object.keys(errors).length > 0}
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SyntheticAssetCreator;